import {
    Policy,
    PolicyRelevantFields,
    PolicyTemplateDetails,
    PolicyTemplateStaticCategory,
    Protocols
} from '../../../types/policies';

import { NullableFields } from '../../../types/types';
import { replaceObjectNullsWithUndefined } from '../../../utils';
import {
    DIRECTORY_TYPES,
    DirectoryTypeValue,
    POLICY_ACTION_TYPES,
    SELECTOR_TYPES
} from '@silverfort-nodelibs/common/utils/consts';
import { UpdatePolicyTemplateBody } from '../api';
import { PricingModules, PricingModulesStatus } from '../../../context/contexts/PricingModuleContext';
import {
    LockedFeatureDisplayConfig,
    SHOWSTOPPER_PROP_OPTIONS,
    TOOLTIP_PROP_OPTIONS
} from '../../common/LockedFeatures';
import { DisabledDisplayOptions, RequiredModuleState } from '../../SystemSettings/hooks/lockedFeatures/types';
import { getActionsForAuthType } from '../../utils';
import { isAnyEnablingModulesActive } from '../../SystemSettings/hooks/lockedFeatures/utils';
import { POLICY_TYPES } from '../PoliciesConsts';
import { RISK_INDICATORS } from '@silverfort-nodelibs/common/utils/insight';

type PolicyBodyData = Pick<Policy, 'name' | 'action_type'> &
    NullableFields<
        Omit<Policy, 'name'> & {
            is_deleted: boolean;
            creation_date: Date;
            update_date: Date;
            hits: number;
            isLoaded: boolean;
            isExpanded: boolean;
            isValid: boolean;
            shouldExpand: boolean;
            users_sum: number;
        }
    >;

type RequiredRelevantPolicyFields = 'policyName' | 'action_type';

type MinimalPolicyDetails = Partial<Omit<PolicyRelevantFields, RequiredRelevantPolicyFields>> &
    Pick<PolicyRelevantFields, RequiredRelevantPolicyFields>;

const getSpnsFromPolicyBodyData = ({ servers, services }: PolicyBodyData): PolicyTemplateDetails['spns'] => {
    if (!servers) {
        return undefined;
    }

    if (!servers.length) {
        return services?.length ? services : undefined;
    }

    const spns = new Set(
        servers.flatMap((server) => server.selectedServices.map((service) => service.service_identifier))
    );

    return spns.size ? Array.from(spns) : undefined;
};

const getSupportedProtocolsFromPolicyBodyData = ({
    kerberos_enabled,
    ntlm_enabled,
    ldap_enabled
}: PolicyBodyData): PolicyTemplateDetails['supportedProtocols'] => {
    const supportedProtocols: PolicyTemplateDetails['supportedProtocols'] = [];

    if (kerberos_enabled || ntlm_enabled) {
        supportedProtocols.push(Protocols.KERBEROS_NTLM);
    }

    if (ldap_enabled) {
        supportedProtocols.push(Protocols.LDAP);
    }

    if (!supportedProtocols.length) {
        return undefined;
    }

    return supportedProtocols;
};

const getAuthTypesFromPolicyBodyData = (policyBodyData: PolicyBodyData): PolicyTemplateDetails['authTypes'] => {
    const INDICATOR_FIELD_NAME_TO_DIRECTORY_TYPE: {
        [key in keyof PolicyBodyData]?: DirectoryTypeValue;
    } = {
        kerberos_enabled: DIRECTORY_TYPES.ad,
        ntlm_enabled: DIRECTORY_TYPES.ad,
        ldap_enabled: DIRECTORY_TYPES.ad,
        oidc_enabled: DIRECTORY_TYPES.aad,
        relying_parties_enabled: DIRECTORY_TYPES.adfs,
        ping_fed_enabled: DIRECTORY_TYPES.pingfed,
        radius_enabled: DIRECTORY_TYPES.radius,
        desktop_enabled: DIRECTORY_TYPES.desktop_mfa,
        okta_enabled: DIRECTORY_TYPES.okta
    };

    const authTypes = Object.entries(INDICATOR_FIELD_NAME_TO_DIRECTORY_TYPE).reduce<Set<string>>(
        (authTypes, [indicator, directoryType]) => {
            return policyBodyData[indicator] ? authTypes.add(directoryType.key) : authTypes;
        },
        new Set<string>()
    );

    return Array.from(authTypes);
};

export const getTemplatePolicyRelevantFieldsFromPolicy = (policyBodyData: PolicyBodyData): MinimalPolicyDetails => {
    const {
        name,
        is_deleted,
        creation_date,
        update_date,
        hits,
        isLoaded,
        isValid,
        isExpanded,
        shouldExpand,
        users_sum,
        ...policyDetails
    } = policyBodyData;
    const { id, company_id, state, ...policyRelevantFields } = replaceObjectNullsWithUndefined(policyDetails);

    return { ...policyRelevantFields, policyName: name };
};

export const getCustomTemplateDetailsFromPolicyBodyData = (
    policyBodyData: PolicyBodyData
): Omit<PolicyTemplateDetails, 'description' | 'descriptionSummary'> => {
    const policyRelevantFields = getTemplatePolicyRelevantFieldsFromPolicy(policyBodyData);
    const { policyName } = policyRelevantFields;
    const { servers } = policyRelevantFields;

    return {
        ...policyRelevantFields,
        policyName,
        name: policyName,
        typeId: PolicyTemplateStaticCategory.CUSTOM,
        isFavorite: false,
        editableFields: [],
        supportedActions: [policyRelevantFields.action_type],
        supportedProtocols: getSupportedProtocolsFromPolicyBodyData(policyBodyData),
        authTypes: getAuthTypesFromPolicyBodyData(policyBodyData),
        spns: servers ? getSpnsFromPolicyBodyData(policyBodyData) : undefined
    };
};

export const buildCustomTemplate = (policyBodyData: PolicyBodyData, description: string): PolicyTemplateDetails => {
    return {
        ...getCustomTemplateDetailsFromPolicyBodyData(policyBodyData),
        description,
        descriptionSummary: description
    };
};

export const getUpdatePolicyTemplateBodyFromPolicyBodyData = (
    policyBodyData: PolicyBodyData
): UpdatePolicyTemplateBody => {
    const {
        is_deleted,
        creation_date,
        update_date,
        hits,
        isLoaded,
        isValid,
        isExpanded,
        shouldExpand,
        users_sum,
        ...policyDetails
    } = policyBodyData;
    const { id, company_id, state, ...policyRelevantFields } = policyDetails;

    return {
        ...policyRelevantFields,
        policyName: policyRelevantFields.name,
        supportedActions: [policyRelevantFields.action_type],
        supportedProtocols: getSupportedProtocolsFromPolicyBodyData(policyBodyData) ?? null,
        authTypes: getAuthTypesFromPolicyBodyData(policyBodyData),
        spns: !!policyRelevantFields.servers ? getSpnsFromPolicyBodyData(policyBodyData) : null
    };
};

export const isPricingModuleEnabled = (
    module: PricingModules,
    pricingModuleState: PricingModulesStatus | null
): boolean => {
    if (!pricingModuleState) return false;

    return isAnyEnablingModulesActive(
        {
            [module]: RequiredModuleState.ON_PREM_ENABLED
        },
        pricingModuleState
    );
};

export const isAuthenticationFirewallEnabled = (pricingModuleState: PricingModulesStatus | null): boolean =>
    isPricingModuleEnabled(PricingModules.AUTHENTICATION_FIREWALL, pricingModuleState);

export const isMfaUsersEnabled = (pricingModuleState: PricingModulesStatus | null): boolean =>
    isPricingModuleEnabled(PricingModules.MFA_USERS, pricingModuleState);

export const isItdrEnabled = (pricingModuleState: PricingModulesStatus | null): boolean =>
    isPricingModuleEnabled(PricingModules.ITDR, pricingModuleState);

export const isIspmEnabled = (pricingModuleState: PricingModulesStatus | null): boolean =>
    isPricingModuleEnabled(PricingModules.ISPM, pricingModuleState);

export const getPolicyHeaderFeatureLockDisplayOptions = (params: {
    policyBodyData: PolicyBodyData;
    hiddenRiskIndicators: string[];
    pricingModules: PricingModulesStatus | null;
}): LockedFeatureDisplayConfig => {
    const { policyBodyData, hiddenRiskIndicators, pricingModules } = params;
    const isAuthenticationModuleEnabled = isAuthenticationFirewallEnabled(pricingModules);
    const isMfaUsersModuleEnabled = isMfaUsersEnabled(pricingModules);
    const isItdrModuleEnabled = isItdrEnabled(pricingModules);
    const isIspmModuleEnabled = isIspmEnabled(pricingModules);

    if (!isAuthenticationModuleEnabled && policyBodyData.action_type === POLICY_ACTION_TYPES.DENY.value) {
        return {
            enabled: false,
            disabledDisplay: DisabledDisplayOptions.TOOLTIP,
            tooltipProps: TOOLTIP_PROP_OPTIONS.AUTHENTICATION_FIREWALL_MODULE_REQUIRED
        };
    }

    if (!isMfaUsersModuleEnabled && policyBodyData.action_type !== POLICY_ACTION_TYPES.DENY.value) {
        return {
            enabled: false,
            disabledDisplay: DisabledDisplayOptions.TOOLTIP,
            tooltipProps: TOOLTIP_PROP_OPTIONS.MFA_MODULE_REQUIRED
        };
    }

    if (policyBodyData.risk_enabled) {
        if (!isItdrModuleEnabled && !isIspmModuleEnabled) {
            return {
                enabled: false,
                disabledDisplay: DisabledDisplayOptions.TOOLTIP,
                tooltipProps: TOOLTIP_PROP_OPTIONS.ITDR_AND_ISPM_MODULES_REQUIRED
            };
        }

        if (
            policyBodyData.risk_indicators?.some(
                (riskIndicator) =>
                    !!RISK_INDICATORS[riskIndicator]?.policyRule &&
                    hiddenRiskIndicators.includes(RISK_INDICATORS[riskIndicator].policyRule as string)
            )
        ) {
            return {
                enabled: false,
                disabledDisplay: DisabledDisplayOptions.TOOLTIP,
                tooltipProps: TOOLTIP_PROP_OPTIONS.ITDR_AND_ISPM_MODULES_REQUIRED
            };
        }
    }

    return {
        enabled: true
    };
};

export const getPolicyTypeOptions = (pricingModules: PricingModulesStatus | null, policyBodyData: PolicyBodyData) => {
    const isRiskEnabled = isItdrEnabled(pricingModules) || isIspmEnabled(pricingModules);

    const policyTypesOptions = Object.values(POLICY_TYPES)
        .map((option) => {
            if (option.value === POLICY_TYPES.RISK_BASED.value && !isRiskEnabled) {
                return {
                    ...option,
                    disabled: true,
                    lockedFeatureOptions: {
                        enabled: false,
                        disabledDisplay: DisabledDisplayOptions.SHOWSTOPPER,
                        showstopperProps: SHOWSTOPPER_PROP_OPTIONS.ITDR_ISPM_MODULES_REQUIRED
                    }
                };
            }

            return option;
        })
        .filter((pt) => {
            if (policyBodyData.action_type === POLICY_ACTION_TYPES.ALLOW.value) {
                return pt.value !== POLICY_TYPES.RISK_BASED.value;
            }
            return true;
        });

    return policyTypesOptions;
};

export const getActionsOptions = (pricingModules: PricingModulesStatus | null, protectedType: number) => {
    const isAuthenticationModuleModuleEnabled = isAuthenticationFirewallEnabled(pricingModules);
    const isUniversalMfaModuleEnabled = isMfaUsersEnabled(pricingModules);

    return getActionsForAuthType(protectedType).map((option) => {
        if (option.value === POLICY_ACTION_TYPES.DENY.value && !isAuthenticationModuleModuleEnabled) {
            return {
                ...option,
                disabled: true,
                lockedFeatureOptions: {
                    enabled: false,
                    disabledDisplay: DisabledDisplayOptions.SHOWSTOPPER,
                    showstopperProps: SHOWSTOPPER_PROP_OPTIONS.AUTHENTICATION_FIREWALL_MODULES_REQUIRED
                }
            };
        }

        if (option.value !== POLICY_ACTION_TYPES.DENY.value && !isUniversalMfaModuleEnabled) {
            return {
                ...option,
                disabled: true,
                lockedFeatureOptions: {
                    enabled: false,
                    disabledDisplay: DisabledDisplayOptions.SHOWSTOPPER,
                    showstopperProps: SHOWSTOPPER_PROP_OPTIONS.MFA_MODULE_REQUIRED
                }
            };
        }

        return option;
    });
};

export const areSomeItemsIncluded = <T>(subset: T[], superset: T[]): boolean => {
    return subset.some((item) => superset.includes(item));
};

export const isS4wlPolicyBodyData = (policyBodyData?: PolicyBodyData): boolean => {
    return !!policyBodyData?.desktop_enabled;
};

export const getPolicyEntitiesModelSelectorType = (policyBodyData?: PolicyBodyData) => {
    if (isS4wlPolicyBodyData(policyBodyData)) {
        return SELECTOR_TYPES.AD_ENTITIES_AND_LOCAL_ACCOUNTS;
    }
    return SELECTOR_TYPES.ENTITIES;
};
